import React, { useState, useEffect } from "react";
import axios from "axios";
import { API_URL, getAuthConfig, labRoleLabel, LAB_ROLE_LABELS } from "./shared";

const LAB_STAFF_ROLES = Object.keys(LAB_ROLE_LABELS);

/*
 * Personnel (cahier des charges §3.3-3.6, §20). Port allégé de StaffModule
 * (frontend/src/pages/HopitalDashboard.js) sur la route dédiée /api/lab/staff
 * (backend/routes/lab_staff.py, PR3) : pas de service_id/matricule/
 * contract_type/weekly_hours/work_rate/disponibilités (LabStaffCreate n'a
 * aucun de ces champs — voir backend/models.py), rôles limités à
 * LAB_ESTABLISHMENT_ROLES.
 *
 * Correctif 422 (déjà connu côté hôpital, weekly_hours/work_rate envoyés ""
 * au lieu de null) : structurellement impossible ici, ces champs n'existent
 * pas dans le formulaire ni dans LabStaffCreate.
 */
export default function StaffModule({ can }) {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [createdInfo, setCreatedInfo] = useState(null);
  const [editHasAccount, setEditHasAccount] = useState(false);
  const emptyForm = { full_name: "", role: "secretaire", phone: "", email: "", status: "active", create_account: false, skills: [] };
  const [form, setForm] = useState(emptyForm);

  const load = () => {
    setLoading(true);
    axios.get(`${API_URL}/api/lab/staff`, getAuthConfig())
      .then((r) => setStaff(r.data.staff || []))
      .catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const resetForm = () => { setForm(emptyForm); setEditing(null); setEditHasAccount(false); setError(""); };

  const save = async () => {
    if (!form.full_name.trim()) { setError("Le nom est obligatoire"); return; }
    if (form.create_account && !form.email.trim()) { setError("Un email est requis pour créer un compte connectable"); return; }
    setSaving(true); setError(""); setCreatedInfo(null);
    try {
      if (editing) {
        const r = await axios.put(`${API_URL}/api/lab/staff/${editing}`, {
          full_name: form.full_name, role: form.role, phone: form.phone, status: form.status,
          create_account: form.create_account, email: form.email, skills: form.skills,
        }, getAuthConfig());
        if (r.data.temp_password) setCreatedInfo({ email: form.email, password: r.data.temp_password });
      } else {
        const r = await axios.post(`${API_URL}/api/lab/staff`, form, getAuthConfig());
        if (r.data.invitation_sent && r.data.staff?.email) setCreatedInfo({ email: r.data.staff.email, password: null });
      }
      setShowForm(false); resetForm(); load();
    } catch (e) {
      setError(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Erreur lors de l'enregistrement");
    } finally { setSaving(false); }
  };

  const startEdit = (m) => {
    setEditing(m.staff_id);
    setEditHasAccount(!!m.user_id);
    setForm({ full_name: m.full_name || "", role: m.role || "secretaire", phone: m.phone || "", email: m.email || "", status: m.status || "active", create_account: false, skills: m.skills || [] });
    setError(""); setShowForm(true);
  };

  const doDelete = async () => {
    if (!toDelete) return;
    try { await axios.delete(`${API_URL}/api/lab/staff/${toDelete.staff_id}`, getAuthConfig()); setToDelete(null); load(); }
    catch (e) { setError("Erreur lors de la suppression"); setToDelete(null); }
  };

  const inputStyle = { width: "100%", padding: "9px 12px", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 14, boxSizing: "border-box", background: "#fff" };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: "#0F172A", margin: 0 }}>Personnel</h1>
        {can("staff:write") && <button onClick={() => { resetForm(); setCreatedInfo(null); setShowForm(true); }}
          style={{ padding: "9px 18px", background: "#185FA5", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>+ Ajouter du personnel</button>}
      </div>

      {createdInfo && (
        <div style={{ background: "#EAF3DE", border: "1px solid #C5E0A5", color: "#3B6D11", borderRadius: 8, padding: "12px 14px", marginTop: 14, fontSize: 13 }}>
          Compte connectable créé{createdInfo.password ? <> — identifiants à transmettre : <b>{createdInfo.email}</b> / mot de passe temporaire : <b>{createdInfo.password}</b></> : <> pour <b>{createdInfo.email}</b>, ses identifiants lui ont été envoyés par email.</>}
        </div>
      )}

      {error && !showForm && (
        <div style={{ background: "#FCEBEB", border: "1px solid #F7C1C1", color: "#A32D2D", borderRadius: 8, padding: "10px 14px", marginTop: 14, fontSize: 13 }}>{error}</div>
      )}

      {showForm && (
        <div style={{ background: "#fff", border: "1px solid #B5D4F4", borderRadius: 12, padding: 20, marginTop: 16 }}>
          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 14, color: "#0F172A" }}>{editing ? "Modifier le personnel" : "Nouveau personnel"}</div>
          {error && <div style={{ background: "#FCEBEB", border: "1px solid #F7C1C1", color: "#A32D2D", borderRadius: 8, padding: "10px 14px", marginBottom: 12, fontSize: 13 }}>{error}</div>}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <input style={inputStyle} placeholder="Nom complet *" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} />
            <select style={inputStyle} value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
              {LAB_STAFF_ROLES.map((r) => <option key={r} value={r}>{labRoleLabel(r)}</option>)}
            </select>
            <input style={inputStyle} placeholder="Téléphone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            <select style={inputStyle} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
              <option value="active">Actif</option>
              <option value="inactive">Inactif</option>
            </select>
          </div>
          {editing && editHasAccount ? (
            <div style={{ marginTop: 12, fontSize: 13, color: "#3B6D11", background: "#F0F7E6", border: "1px solid #D6E8BF", borderRadius: 8, padding: "8px 12px" }}>
              ✓ Ce membre a déjà un compte connectable.
            </div>
          ) : (
            <div style={{ marginTop: 12 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#334155", cursor: "pointer" }}>
                <input type="checkbox" checked={form.create_account} onChange={(e) => setForm({ ...form, create_account: e.target.checked })} />
                {editing ? "Activer un compte connectable (l'employé pourra se connecter)" : "Créer un compte connectable (l'employé pourra se connecter à l'espace)"}
              </label>
              {form.create_account && (
                <input style={{ ...inputStyle, marginTop: 10 }} placeholder="Email * (pour le compte)" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              )}
            </div>
          )}
          <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: 12, color: "#64748B", marginBottom: 5 }}>Compétences</div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {["Prélèvement pédiatrique", "Prélèvement veineux difficile", "ECG", "Analyses spécialisées", "Accueil"].map((sk) => {
                const on = (form.skills || []).includes(sk);
                return <button key={sk} type="button" onClick={() => setForm({ ...form, skills: on ? form.skills.filter((x) => x !== sk) : [...(form.skills || []), sk] })} style={{ padding: "4px 10px", borderRadius: 14, fontSize: 12, cursor: "pointer", border: "1px solid " + (on ? "#185FA5" : "#CBD5E1"), background: on ? "#E6F1FB" : "#fff", color: on ? "#185FA5" : "#64748B" }}>{sk}</button>;
              })}
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
            <button onClick={save} disabled={saving} style={{ padding: "8px 18px", background: "#185FA5", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>{saving ? "Enregistrement..." : "Enregistrer"}</button>
            <button onClick={() => { setShowForm(false); resetForm(); }} style={{ padding: "8px 18px", background: "transparent", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>Annuler</button>
          </div>
        </div>
      )}

      <div style={{ marginTop: 20, background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12, overflow: "hidden" }}>
        {loading ? (
          <div style={{ padding: 30, textAlign: "center", color: "#94A3B8", fontSize: 14 }}>Chargement...</div>
        ) : staff.length === 0 ? (
          <div style={{ padding: 30, textAlign: "center", color: "#94A3B8", fontSize: 14 }}>Aucun personnel. Cliquez sur « Ajouter du personnel ».</div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#F8FAFC", textAlign: "left" }}>
                {["Nom", "Rôle", "Téléphone", "Statut", "Compte", "Actions"].map((h) => (
                  <th key={h} style={{ padding: "10px 16px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {staff.map((m) => (
                <tr key={m.staff_id} style={{ borderTop: "1px solid #F1F5F9" }}>
                  <td style={{ padding: "12px 16px", fontSize: 14, fontWeight: 500, color: "#0F172A" }}>{m.full_name}</td>
                  <td style={{ padding: "12px 16px", fontSize: 13, color: "#475569" }}>{labRoleLabel(m.role)}</td>
                  <td style={{ padding: "12px 16px", fontSize: 13, color: "#475569" }}>{m.phone || "—"}</td>
                  <td style={{ padding: "12px 16px", fontSize: 13 }}>{m.status === "inactive" ? <span style={{ color: "#94A3B8" }}>Inactif</span> : <span style={{ color: "#3B6D11" }}>Actif</span>}</td>
                  <td style={{ padding: "12px 16px", fontSize: 13 }}>{m.user_id ? <span style={{ color: "#3B6D11" }}>Oui</span> : <span style={{ color: "#94A3B8" }}>—</span>}</td>
                  <td style={{ padding: "12px 16px" }}>
                    <span style={{ display: "flex", gap: 12 }}>
                      {can("staff:write") && <><button onClick={() => startEdit(m)} style={{ fontSize: 13, color: "#185FA5", background: "none", border: "none", cursor: "pointer" }}>Modifier</button>
                      <button onClick={() => setToDelete(m)} style={{ fontSize: 13, color: "#A32D2D", background: "none", border: "none", cursor: "pointer" }}>Supprimer</button></>}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {toDelete && (
        <div onClick={() => setToDelete(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 12, width: "90%", maxWidth: 400, padding: 24 }}>
            <div style={{ fontSize: 16, fontWeight: 600, color: "#0F172A", marginBottom: 8 }}>Supprimer ce membre ?</div>
            <div style={{ fontSize: 13, color: "#64748B", marginBottom: 20, lineHeight: 1.5 }}><b>{toDelete.full_name}</b> sera retiré de l'annuaire et perdra immédiatement tout accès à cet établissement. Son compte de connexion éventuel (utilisable ailleurs) n'est pas supprimé, mais son accès ici est bien révoqué.</div>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button onClick={() => setToDelete(null)} style={{ fontSize: 13, padding: "8px 16px", borderRadius: 8, border: "1px solid #CBD5E1", background: "#fff", color: "#64748B", cursor: "pointer" }}>Annuler</button>
              <button onClick={doDelete} style={{ fontSize: 13, padding: "8px 16px", borderRadius: 8, border: "none", background: "#A32D2D", color: "#fff", cursor: "pointer" }}>Supprimer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
